import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import PersonaAvatar from '../../components/PersonaAvatar'

interface Project {
  id: string
  title: string
  description?: string
  lead?: string
  status?: string
  tasks?: { total: number; completed: number }
}

const PROJECT_IMAGES: Record<string, { emoji: string; gradient: string }> = {
  'Brand Identity': { emoji: '🎨', gradient: 'linear-gradient(135deg, #C9A66B 0%, #8B6914 100%)' },
  'Website Rebuild': { emoji: '🌐', gradient: 'linear-gradient(135deg, #4A90D9 0%, #1B3A5C 100%)' },
  'Social Media Strategy': { emoji: '📱', gradient: 'linear-gradient(135deg, #E1306C 0%, #833AB4 100%)' },
  'Booking System': { emoji: '📅', gradient: 'linear-gradient(135deg, #34C759 0%, #1B7A3D 100%)' },
  'Speakeasy Deck': { emoji: '🎬', gradient: 'linear-gradient(135deg, #FF6B35 0%, #A83E1C 100%)' },
  'Client Communication': { emoji: '💬', gradient: 'linear-gradient(135deg, #5856D6 0%, #2D2B8A 100%)' },
  'Welcome & Onboarding': { emoji: '👋', gradient: 'linear-gradient(135deg, #FFD60A 0%, #B8960A 100%)' },
}

const DEFAULT_IMAGE = { emoji: '📋', gradient: 'linear-gradient(135deg, #666 0%, #333 100%)' }

function ExpandableDescription({ text, maxLength = 100 }: { text: string; maxLength?: number }) {
  const [expanded, setExpanded] = useState(false)
  const needsTruncation = text.length > maxLength

  return (
    <p style={{
      fontSize: 13,
      color: 'var(--text-secondary)',
      margin: '0 0 16px 0',
      lineHeight: 1.5,
    }}>
      {needsTruncation && !expanded ? (
        <>
          {text.slice(0, maxLength).trim()}...{' '}
          <span
            onClick={e => { e.preventDefault(); e.stopPropagation(); setExpanded(true) }}
            style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }}
          >
            more
          </span>
        </>
      ) : (
        <>
          {text}
          {needsTruncation && (
            <>
              {' '}
              <span
                onClick={e => { e.preventDefault(); e.stopPropagation(); setExpanded(false) }}
                style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }}
              >
                less
              </span>
            </>
          )}
        </>
      )}
    </p>
  )
}

export default function CommunityProjectsPage() {
  const { user } = useAuth()
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)

  const communityId = user?.communityId || ''
  const communityName = user?.communityName || 'Projects'
  const token = localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]

  useEffect(() => {
    if (!communityId) return
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
    fetch(`/api/communities/${communityId}/projects`, { headers, credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        const communityProjects = Array.isArray(data) ? data : (data.projects || [])
        setProjects(communityProjects)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [communityId, token])

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Loading projects...</div>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 24px' }}>
      <div style={{ marginBottom: 40 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
          Your Projects
        </h1>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 8 }}>
          Everything we're building for {communityName}
        </p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: 20,
      }}>
        {projects.map(p => {
          const img = PROJECT_IMAGES[p.title] || DEFAULT_IMAGE
          return (
            <Link
              key={p.id}
              to={`/project/${p.id}`}
              style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column' as const, height: '100%' }}
            >
            <div
              style={{
                borderRadius: 16,
                overflow: 'hidden',
                background: 'var(--bg-elevated, #1a1a1a)',
                border: '1px solid var(--border-subtle, #333)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column' as const,
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-4px)'
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.3)'
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = 'none'
              }}
            >
              <div style={{
                height: 140,
                background: img.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 48,
              }}>
                {img.emoji}
              </div>

              <div style={{ padding: '20px 20px 24px', flex: 1, display: 'flex', flexDirection: 'column' as const }}>
                <h3 style={{
                  fontSize: 17,
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  margin: '0 0 8px 0',
                }}>
                  {p.title}
                </h3>

                {p.description && <ExpandableDescription text={p.description} />}

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 'auto', paddingTop: 12 }}>
                  {p.lead && <PersonaAvatar name={p.lead} size={22} />}
                  {p.lead && (
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {p.lead.charAt(0).toUpperCase() + p.lead.slice(1)}
                    </span>
                  )}
                  <span style={{
                    marginLeft: 'auto',
                    fontSize: 11,
                    padding: '3px 10px',
                    borderRadius: 12,
                    background: p.status === 'active' ? 'rgba(52,199,89,0.15)' : 'rgba(255,255,255,0.08)',
                    color: p.status === 'active' ? '#34C759' : 'var(--text-secondary)',
                    fontWeight: 500,
                  }}>
                    {p.status === 'active' ? 'Active' : p.status || 'Active'}
                  </span>
                </div>
              </div>
            </div>
            </Link>
          )
        })}
      </div>

      {projects.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
          No projects yet. Your team is getting things set up.
        </div>
      )}
    </div>
  )
}
